/**
 * Chat Service
 * Handles chat message API calls (text and file uploads)
 */

import { AxiosError, AxiosProgressEvent } from 'axios';
import { uploadClient } from 'services/apiClient';
import { API_ENDPOINTS } from 'config/api.config';
import { validateFile } from 'services/documentService';
import useAuthStore from 'stores/authStore';
import { ChatMessageResponse } from 'types/chat.types';
import { DocumentServiceError } from 'types/document.types';

/**
 * Chat Service Class
 */
class ChatService {
  /**
   * Send a chat message (text-only or with file)
   * @param message - Text message
   * @param file - Optional file to upload
   * @param onUploadProgress - Optional progress callback
   * @param conversationId - Optional conversation ID to continue existing conversation
   * @returns Promise with chat response (immediate or job-based)
   */
  async sendMessage(
    message: string,
    file?: File,
    onUploadProgress?: ((progress: number) => void) | null,
    conversationId?: string | null,
    options?: { skipTariff?: boolean },
    additionalFiles?: File[]
  ): Promise<ChatMessageResponse> {
    console.log('[chatService] sendMessage called', {
      messageLength: message?.length,
      hasFile: !!file,
      fileName: file?.name,
      additionalFileCount: additionalFiles?.length ?? 0
    });

    // Validate all files
    const allFiles = [file, ...(additionalFiles || [])].filter(Boolean) as File[];
    for (const f of allFiles) {
      const validation = validateFile(f);
      if (!validation.isValid) {
        console.error('❌ [chatService] File validation failed:', f.name, validation.errors);
        throw new Error(validation.errors.join(', '));
      }
    }
    if (allFiles.length > 0) {
      console.log('✅ [chatService] All files validated:', allFiles.map(f => f.name));
    }

    const formData = new FormData();

    // Add message
    if (message && message.trim()) {
      console.log('[chatService] Adding message to FormData');
      formData.append('message', message.trim());
    }

    // Add primary file
    if (file) {
      console.log('[chatService] Adding primary file to FormData:', file.name);
      formData.append('file', file);
    }

    // Add additional files (multi-file upload)
    if (additionalFiles && additionalFiles.length > 0) {
      for (const extraFile of additionalFiles) {
        console.log('[chatService] Adding additional file to FormData:', extraFile.name);
        formData.append('files', extraFile);
      }
    }

    // Add user_id from auth store
    const userId = useAuthStore.getState().getUserId();
    if (userId) {
      console.log('[chatService] Adding user_id:', userId);
      formData.append('user_id', userId);
    } else {
      console.warn('⚠️ [chatService] No user_id found');
    }

    // Add conversation_id if provided (to continue existing conversation)
    if (conversationId) {
      console.log('[chatService] Adding conversation_id:', conversationId);
      formData.append('conversation_id', conversationId);
    }

    if (options?.skipTariff) {
      formData.append('skip_tariff', 'true');
    }

    try {
      console.log('[chatService] Sending POST request to:', API_ENDPOINTS.CHAT.MESSAGE);
      const response = await uploadClient.post<ChatMessageResponse>(
        API_ENDPOINTS.CHAT.MESSAGE,
        formData,
        {
          onUploadProgress: file && onUploadProgress ? (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              console.log('[chatService] Upload progress:', percentCompleted + '%');
              onUploadProgress(percentCompleted);
            }
          } : undefined,
        }
      );

      console.log('✅ [chatService] Response received:', {
        type: response.data.type,
        hasAnswer: !!response.data.answer,
        hasJobId: !!(response.data as any).job_id
      });
      console.log('[chatService] Full response data:', response.data);

      return response.data;
    } catch (error) {
      console.error('❌ [chatService] Request failed:', error);
      throw this.handleError(error as AxiosError);
    }
  }

  /**
   * Error handler
   * @param error - Axios error object
   * @returns Formatted error with additional properties
   */
  handleError(error: AxiosError): DocumentServiceError {
    if (error.response) {
      const { status, data } = error.response;
      const message = (data as any)?.message || (data as any)?.error || 'An error occurred';

      const customError = new Error(message) as DocumentServiceError;
      customError.status = status;
      customError.data = data;

      return customError;
    } else if (error.request) {
      const customError = new Error('No response from server. Please check your connection.') as DocumentServiceError;
      return customError;
    } else {
      return error as DocumentServiceError;
    }
  }
}

// Create and export singleton instance
const chatService = new ChatService();

export default chatService;
